import { useEffect, useState, useCallback } from "react";
import { crDate, crDateTime } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Trash2, Mail, MessageCircle, MapPin, Layers, Link2, Loader2, User, Circle,
  Clock, FileText, Save, ExternalLink, Phone, UserPlus, CheckCircle2, AlertTriangle, MessageSquare, Handshake,
} from "lucide-react";

const STATUSES = ["new", "introductory_class", "contacted", "interview_meeting",
  "send_info", "info_ready", "enrolled", "later", "not_interested"];

// Friendly labels for known CF7 field names. Any field not listed here will
// fall back to a nicely humanized version of its raw name (e.g.
// "your-primary-challenge" → "Your primary challenge").
const CUSTOM_FIELD_LABELS = {
  "your-primary-challenge": "Reto principal para aprender español",
  "why-learning-Spanish-is-important": "Por qué es importante aprender español",
  "why-learning-spanish-is-important": "Por qué es importante aprender español",
  "your-motivation": "Motivación",
  "your-goal": "Meta",
  "your-goals": "Metas",
  "your-level": "Nivel actual",
  "nivel_espanol": "Nivel de español",
  "quien_recomendo": "Quién nos recomendó",
  "who_recommended": "Quién nos recomendó",
  "your-availability": "Disponibilidad",
};

function formatFieldLabel(raw) {
  if (!raw) return "";
  const key = String(raw).toLowerCase();
  if (CUSTOM_FIELD_LABELS[raw]) return CUSTOM_FIELD_LABELS[raw];
  if (CUSTOM_FIELD_LABELS[key]) return CUSTOM_FIELD_LABELS[key];
  // Generic humanizer: strip common CF7 prefixes and turn dashes/underscores into spaces.
  let s = String(raw).replace(/^your[-_]?/i, "").replace(/[-_]+/g, " ").trim();
  if (!s) s = String(raw);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const statusBadgeStyles = {
  new: "bg-blue-100 text-blue-700",
  introductory_class: "bg-teal-100 text-teal-700",
  contacted: "bg-amber-100 text-amber-700",
  interview_meeting: "bg-violet-100 text-violet-700",
  send_info: "bg-indigo-100 text-indigo-700",
  info_ready: "bg-cyan-100 text-cyan-700",
  enrolled: "bg-emerald-100 text-emerald-700",
  later: "bg-slate-200 text-slate-700",
  not_interested: "bg-rose-100 text-rose-700",
};

function formatDateTime(iso) {
  if (!iso) return "";
  return crDateTime(iso) || iso;
}

function EventItem({ event, t }) {
  const kindLabel = t(`lead_event_${event.kind}`) || event.kind;
  const iconByKind = {
    created: <Circle className="h-3 w-3 text-blue-500" />,
    auto_response_sent: <Mail className="h-3 w-3 text-emerald-500" />,
    status_changed: <ExternalLink className="h-3 w-3 text-violet-500" />,
    notes_updated: <FileText className="h-3 w-3 text-slate-500" />,
    note: <FileText className="h-3 w-3 text-slate-500" />,
    call: <Phone className="h-3 w-3 text-amber-500" />,
    email_sent: <Mail className="h-3 w-3 text-emerald-500" />,
    whatsapp_sent: <MessageCircle className="h-3 w-3 text-green-500" />,
    assigned: <User className="h-3 w-3 text-blue-500" />,
  };
  const icon = iconByKind[event.kind] || <Circle className="h-3 w-3 text-slate-400" />;
  let detail = "";
  if (event.kind === "status_changed") {
    detail = `${t(`lead_status_${event.payload?.from}`) || event.payload?.from} → ${t(`lead_status_${event.payload?.to}`) || event.payload?.to}`;
  } else if (event.payload?.content) {
    detail = event.payload.content;
  } else if (event.payload?.notes) {
    detail = event.payload.notes;
  } else if (event.payload?.email_id) {
    detail = `→ Reply-To: ${event.payload.reply_to || ""}`;
  } else if (event.payload?.campus) {
    detail = `${event.payload.campus}${event.payload.group ? " · " + event.payload.group : ""}`;
  }

  return (
    <div className="flex gap-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={`event-${event.id}`}>
      <div className="mt-1 flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-slate-700">{kindLabel}</div>
        {detail && <div className="text-xs text-slate-500 mt-0.5 break-words">{detail}</div>}
        <div className="text-[10px] text-slate-400 mt-0.5">{formatDateTime(event.created_at)}</div>
      </div>
    </div>
  );
}

export default function LeadDetailDrawer({ leadId, onClose, onLeadUpdated, onLeadDeleted, onOpenLead }) {
  const { t } = useI18n();
  const [lead, setLead] = useState(null);
  const [events, setEvents] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [convForm, setConvForm] = useState({ group_id: "", start_date: "", amount: "", classes_included: "", paid_on_start: false, send_welcome_email: true });
  const [converting, setConverting] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", last_name: "", email: "", whatsapp: "", campus: "", group: "", referred: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [assigningAffiliate, setAssigningAffiliate] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data.lead);
      setEvents(data.events || []);
      setDuplicates(data.duplicates || []);
      setNotes(data.lead?.notes || "");
      // Load email tracking history in parallel (best effort)
      try {
        const eh = await api.get(`/leads/${leadId}/email-history`);
        setEmailHistory(eh.data.items || []);
      } catch { setEmailHistory([]); }
    } catch (e) {
      toast.error("Error loading lead");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (leadId) load();
    else {
      setLead(null);
      setEvents([]);
      setEmailHistory([]);
      setNotes("");
      setNewNote("");
    }
  }, [leadId, load]);

  useEffect(() => {
    if (!leadId) return;
    api.get("/affiliates").then((r) => setAffiliates(r.data || [])).catch(() => setAffiliates([]));
  }, [leadId]);

  const assignAffiliate = async (value) => {
    const code = value === "none" ? "" : value;
    if ((code || null) === (lead?.affiliate_code || null)) return;
    setAssigningAffiliate(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { affiliate_code: code });
      setLead(data.lead);
      onLeadUpdated && onLeadUpdated(data.lead);
      await load();
      toast.success(code ? "Afiliado asignado al lead" : "Afiliado removido del lead");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error asignando afiliado");
    } finally {
      setAssigningAffiliate(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!lead || newStatus === lead.status) return;
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { status: newStatus });
      setLead(data.lead);
      onLeadUpdated && onLeadUpdated(data.lead);
      await load();
      toast.success(t("lead_pipeline_moved").replace("{status}", t(`lead_status_${newStatus}`)));
    } catch {
      toast.error("Error updating status");
    }
  };

  const saveNotes = async () => {
    if (!lead) return;
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { notes });
      setLead(data.lead);
      onLeadUpdated && onLeadUpdated(data.lead);
      await load();
      toast.success(t("lead_notes_saved"));
    } catch {
      toast.error("Error saving notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !lead) return;
    setAddingNote(true);
    try {
      await api.post(`/leads/${lead.id}/events`, { kind: "note", content: newNote.trim() });
      setNewNote("");
      await load();
    } catch {
      toast.error("Error adding note");
    } finally {
      setAddingNote(false);
    }
  };

  const deleteLead = async () => {
    if (!lead) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      onLeadDeleted && onLeadDeleted(lead.id);
      toast.success("Deleted");
    } catch {
      toast.error("Error deleting");
    }
  };

  const openConvert = async () => {
    setConvertOpen(true);
  };

  const openEdit = () => {
    if (!lead) return;
    setEditForm({
      name: lead.name || "",
      last_name: lead.last_name || "",
      email: lead.email || "",
      whatsapp: lead.whatsapp || "",
      campus: lead.campus || "general",
      group: lead.group || "",
      referred: lead.referred || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editForm.name.trim()) {
      toast.error(t("lead_edit_name_required"));
      return;
    }
    if (!editForm.email.trim()) {
      toast.error(t("lead_edit_email_required"));
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, {
        name: editForm.name.trim(),
        last_name: editForm.last_name.trim() || null,
        email: editForm.email.trim(),
        whatsapp: editForm.whatsapp.trim() || null,
        campus: editForm.campus || "general",
        group: editForm.group.trim() || null,
        referred: editForm.referred.trim() || null,
      });
      setLead(data.lead);
      onLeadUpdated && onLeadUpdated(data.lead);
      setEditOpen(false);
      await load();
      toast.success(t("lead_edit_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("lead_edit_error"));
    } finally {
      setSavingEdit(false);
    }
  };

  const submitConvert = async () => {
    setConverting(true);
    try {
      // Quick convert: only creates the student account + sends welcome email.
      // Enrollment happens later from Students → Edit → Add enrollment.
      await api.post(`/leads/${lead.id}/convert`, { send_welcome_email: true });
      toast.success(t("lead_convert_success"));
      setConvertOpen(false);
      await load();
      onLeadUpdated && onLeadUpdated({ ...lead, status: "enrolled" });
    } catch (e) {
      const msg = e?.response?.data?.detail || t("lead_convert_error");
      toast.error(msg);
    } finally {
      setConverting(false);
    }
  };

  const whatsappLink = lead?.whatsapp
    ? `https://wa.me/${lead.whatsapp.replace(/[^\d]/g, "")}`
    : null;
  // Open Gmail compose in a new tab (more reliable than mailto: which relies
  // on a system default mail client being configured).
  const gmailLink = lead?.email
    ? `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(lead.email)}`
    : null;

  return (
    <Sheet open={!!leadId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="lead-drawer">
        {loading || !lead ? (
          <div className="p-12 text-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : (
          <>
            <SheetHeader className="p-6 pb-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <SheetTitle className="text-xl font-semibold truncate" data-testid="lead-detail-name">
                    {[lead.name, lead.last_name].filter(Boolean).join(" ")}
                  </SheetTitle>
                  <div className="text-sm text-slate-500 truncate mt-0.5">{lead.email}</div>
                </div>
                <Badge className={statusBadgeStyles[lead.status]} data-testid="lead-detail-status-badge">
                  {t(`lead_status_${lead.status}`)}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {gmailLink && (
                  <Button asChild size="sm" variant="outline" data-testid="lead-btn-email">
                    <a href={gmailLink} target="_blank" rel="noreferrer">
                      <Mail className="h-3.5 w-3.5 mr-1.5" />{t("lead_open_email")}
                    </a>
                  </Button>
                )}
                {whatsappLink && (
                  <Button asChild size="sm" variant="outline" data-testid="lead-btn-whatsapp">
                    <a href={whatsappLink} target="_blank" rel="noreferrer">
                      <MessageCircle className="h-3.5 w-3.5 mr-1.5" />{t("lead_open_whatsapp")}
                    </a>
                  </Button>
                )}
                {lead.status !== "enrolled" && !lead.converted_to_user_id && (
                  <Button size="sm" onClick={openConvert} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lead-btn-convert">
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />{t("lead_convert")}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={openEdit} data-testid="lead-btn-edit">
                  <FileText className="h-3.5 w-3.5 mr-1.5" />{t("lead_edit")}
                </Button>
                {lead.converted_to_user_id && (
                  <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1" data-testid="lead-converted-badge">
                    <CheckCircle2 className="h-3 w-3" />{t("lead_converted_badge")}
                  </Badge>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto" data-testid="lead-btn-delete">
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />{t("lead_delete")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("lead_delete")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("lead_delete_confirm")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("template_cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={deleteLead} className="bg-rose-600 hover:bg-rose-700" data-testid="lead-btn-delete-confirm">
                        {t("lead_delete")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Convert dialog */}
              <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
                <DialogContent data-testid="lead-convert-dialog">
                  <DialogHeader>
                    <DialogTitle>{t("lead_convert_title")}</DialogTitle>
                    <DialogDescription>{t("lead_convert_desc_v2")}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3.5 text-sm space-y-1.5">
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{t("lead_convert_summary")}</div>
                      <div className="text-slate-800"><strong>{[lead?.name, lead?.last_name].filter(Boolean).join(" ") || lead?.name || "—"}</strong></div>
                      <div className="text-slate-600">📧 {lead?.email}</div>
                      {lead?.whatsapp && <div className="text-slate-600">📱 {lead.whatsapp}</div>}
                    </div>
                    <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 leading-relaxed">
                      <div className="font-semibold mb-1">{t("lead_convert_next_steps_title")}</div>
                      <ol className="list-decimal ml-4 space-y-0.5">
                        <li>{t("lead_convert_step1")}</li>
                        <li>{t("lead_convert_step2")}</li>
                        <li>{t("lead_convert_step3")}</li>
                      </ol>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setConvertOpen(false)}>{t("template_cancel")}</Button>
                    <Button onClick={submitConvert} disabled={converting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="convert-submit-btn">
                      {converting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
                      {t("lead_convert_confirm")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Edit contact info dialog */}
              <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent data-testid="lead-edit-dialog">
                  <DialogHeader>
                    <DialogTitle>{t("lead_edit_title")}</DialogTitle>
                    <DialogDescription>{t("lead_edit_desc")}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>{t("lead_edit_name")}</Label>
                        <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" data-testid="edit-name" />
                      </div>
                      <div>
                        <Label>{t("lead_edit_last_name")}</Label>
                        <Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} className="mt-1" data-testid="edit-last-name" />
                      </div>
                    </div>
                    <div>
                      <Label>{t("lead_edit_email")}</Label>
                      <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1" data-testid="edit-email" />
                    </div>
                    <div>
                      <Label>{t("lead_edit_whatsapp")}</Label>
                      <Input value={editForm.whatsapp} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })} className="mt-1" placeholder="+506 8888-8888" data-testid="edit-whatsapp" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>{t("lead_edit_campus")}</Label>
                        <Select value={editForm.campus} onValueChange={(v) => setEditForm({ ...editForm, campus: v })}>
                          <SelectTrigger className="mt-1" data-testid="edit-campus"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["general","santa_ana","atenas","grecia","online"].map((c) => (
                              <SelectItem key={c} value={c}>{t(`lead_campus_${c}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{t("lead_edit_group")}</Label>
                        <Input value={editForm.group} onChange={(e) => setEditForm({ ...editForm, group: e.target.value })} className="mt-1" data-testid="edit-group" />
                      </div>
                    </div>
                    <div>
                      <Label>{t("lead_edit_referred")}</Label>
                      <Input value={editForm.referred} onChange={(e) => setEditForm({ ...editForm, referred: e.target.value })} className="mt-1" data-testid="edit-referred" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEditOpen(false)}>{t("template_cancel")}</Button>
                    <Button onClick={submitEdit} disabled={savingEdit} className="bg-brand-blue" data-testid="edit-submit-btn">
                      {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                      {t("lead_edit_save")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </SheetHeader>

            {/* Duplicate leads warning banner */}
            {(lead?.email_status === "bounced" || lead?.email_status === "complained") && (
              <div
                className="mx-6 mt-4 rounded-lg border-2 border-rose-300 bg-rose-50 p-3.5"
                data-testid="lead-email-bounced-banner"
              >
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-rose-900">
                      {lead.email_status === "bounced" ? t("lead_email_bounced_title") : t("lead_email_complained_title")}
                    </div>
                    <div className="text-rose-800 text-xs mt-0.5">
                      {t("lead_email_invalid_desc")}
                      {lead.email_status_reason && <span className="font-mono ml-1">({lead.email_status_reason})</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {duplicates.length > 0 && (
              <div
                className="mx-6 mt-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-3.5"
                data-testid="lead-duplicates-banner"
              >
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-amber-900">
                      {t("lead_duplicates_title").replace("{count}", duplicates.length)}
                    </div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      {t("lead_duplicates_desc")}
                    </div>
                    <div className="mt-2.5 space-y-1.5">
                      {duplicates.slice(0, 5).map((d) => (
                        <button
                          key={d.id}
                          onClick={() => onOpenLead?.(d.id)}
                          className="w-full text-left px-2 py-1.5 rounded-md bg-white border border-amber-200 hover:border-amber-400 hover:bg-amber-100/50 transition-colors flex items-center justify-between gap-2 text-xs"
                          data-testid={`duplicate-item-${d.id}`}
                        >
                          <span className="truncate">
                            <strong className="text-slate-800">
                              {[d.name, d.last_name].filter(Boolean).join(" ") || d.email || d.whatsapp}
                            </strong>
                            {d.group && <span className="text-slate-500"> · {d.group}</span>}
                          </span>
                          <span className="shrink-0 text-slate-400">
                            {crDate(d.created_at)}
                          </span>
                        </button>
                      ))}
                      {duplicates.length > 5 && (
                        <div className="text-xs text-amber-700 italic px-2">
                          + {duplicates.length - 5} {t("lead_duplicates_more")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="p-6 space-y-6">
              {/* Info */}
              <div className="space-y-2 text-sm">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">{t("leads")}</div>
                {lead.whatsapp && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Phone className="h-3.5 w-3.5 text-slate-400" />
                    <span>{lead.whatsapp}</span>
                  </div>
                )}
                {lead.campus && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" />
                    <span>{t(`lead_campus_${lead.campus}`)}</span>
                  </div>
                )}
                {lead.group && (
                  <div className="flex items-start gap-2 text-slate-700">
                    <Layers className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
                    <span>{lead.group}</span>
                  </div>
                )}
                {lead.referred && (
                  <div className="flex items-start gap-2 text-slate-700">
                    <User className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
                    <span className="text-slate-500">{t("lead_referred")}:</span>
                    <span>{lead.referred}</span>
                  </div>
                )}
                <div className="flex items-start gap-2 mt-2 p-2.5 bg-brand-blue/5 rounded-md border border-brand-blue/20" data-testid="lead-detail-affiliate-block">
                  <Handshake className="h-4 w-4 text-brand-blue mt-0.5 flex-shrink-0" />
                  <div className="text-sm min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-brand-blue font-semibold">Afiliado</div>
                    {lead.affiliate_code ? (
                      <>
                        <div className="text-slate-900 font-mono text-xs mt-0.5">
                          {lead.affiliate_code}
                          {(() => { const a = affiliates.find((x) => x.code === lead.affiliate_code); return a ? ` — ${a.name}` : ""; })()}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Este lead vino a través de un afiliado. Se generará una comisión cuando pague el primer ciclo.</div>
                      </>
                    ) : (
                      <div className="text-[11px] text-slate-500 mt-0.5">Este lead no tiene afiliado. Si llegó por recomendación de un afiliado pero sin su link, podés asignarlo aquí.</div>
                    )}
                    <Select
                      value={lead.affiliate_code || "none"}
                      onValueChange={assignAffiliate}
                      disabled={assigningAffiliate}
                    >
                      <SelectTrigger className="mt-2 h-8 text-xs bg-white" data-testid="lead-affiliate-select">
                        <SelectValue placeholder="Asignar afiliado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin afiliado</SelectItem>
                        {affiliates.filter((a) => a.active !== false).map((a) => (
                          <SelectItem key={a.code} value={a.code}>{a.name} ({a.code})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {lead.source_url && (
                  <div className="flex items-start gap-2 text-slate-700">
                    <Link2 className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
                    <a href={lead.source_url} target="_blank" rel="noreferrer" className="text-brand-blue hover:underline text-xs break-all">
                      {lead.source_url}
                    </a>
                  </div>
                )}
                {lead.message && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-md text-slate-700 text-sm border border-slate-200">
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1">{t("lead_message")}</div>
                    {lead.message}
                  </div>
                )}
                {lead.custom_fields && Object.keys(lead.custom_fields).length > 0 && (
                  <div className="mt-3 p-3 bg-blue-50/50 rounded-md text-slate-700 text-sm border border-blue-200">
                    <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-2 flex items-center gap-1.5">
                      <MessageSquare className="h-3.5 w-3.5" />
                      {t("lead_form_answers")}
                    </div>
                    <div className="space-y-2.5">
                      {Object.entries(lead.custom_fields).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-xs font-medium text-slate-500">{formatFieldLabel(key)}</div>
                          <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status selector */}
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-2">
                  {t("lead_status")}
                </label>
                <Select value={lead.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-full" data-testid="lead-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{t(`lead_status_${s}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Internal notes */}
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-2">
                  {t("lead_notes")}
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("lead_notes_placeholder")}
                  rows={3}
                  data-testid="lead-notes-textarea"
                />
                {notes !== (lead.notes || "") && (
                  <Button size="sm" onClick={saveNotes} disabled={savingNotes} className="mt-2" data-testid="lead-save-notes">
                    {savingNotes ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    {t("lead_save_note")}
                  </Button>
                )}
              </div>

              {/* Add event */}
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-2">
                  {t("lead_add_note")}
                </label>
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder={t("lead_note_placeholder")}
                  rows={2}
                  data-testid="lead-newnote-textarea"
                />
                <Button size="sm" onClick={addNote} disabled={!newNote.trim() || addingNote} className="mt-2" data-testid="lead-add-note-btn">
                  {addingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                  {t("lead_save_note")}
                </Button>
              </div>

              {/* Email history (Resend tracking) */}
              {emailHistory.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">
                    <Mail className="h-3.5 w-3.5" />
                    {t("lead_email_history")}
                  </div>
                  <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="lead-email-history">
                    {emailHistory.map((rec) => {
                      const styles = {
                        sent:       { bg: "bg-slate-100 text-slate-700",   label: t("email_status_sent") },
                        delivered:  { bg: "bg-blue-100 text-blue-700",     label: t("email_status_delivered") },
                        opened:     { bg: "bg-emerald-100 text-emerald-700", label: t("email_status_opened") },
                        clicked:    { bg: "bg-violet-100 text-violet-700",   label: t("email_status_clicked") },
                        bounced:    { bg: "bg-rose-100 text-rose-700",       label: t("email_status_bounced") },
                        complained: { bg: "bg-orange-100 text-orange-700",   label: t("email_status_complained") },
                        failed:     { bg: "bg-rose-100 text-rose-700",       label: t("email_status_failed") },
                      };
                      const s = styles[rec.status] || styles.sent;
                      return (
                        <div key={rec.id} className="p-2.5 text-sm">
                          <div className="flex items-start gap-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.bg} flex-shrink-0`}>
                              {s.label}
                              {rec.open_count > 1 && ` ×${rec.open_count}`}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-slate-800 truncate" title={rec.subject}>{rec.subject}</div>
                              <div className="text-[11px] text-slate-500">
                                {t(`email_campaign_${rec.campaign_type}`) || rec.campaign_type}
                                {" · "}{crDateTime(rec.sent_at)}
                              </div>
                              {rec.status === "bounced" && rec.bounce_reason && (
                                <div className="text-[11px] text-rose-600 mt-0.5">{rec.bounce_reason}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">
                  <Clock className="h-3.5 w-3.5" />
                  {t("lead_timeline")}
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="lead-timeline">
                  {events.slice().reverse().map((e) => (
                    <EventItem key={e.id} event={e} t={t} />
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
